/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

// This file co-locates content-asset-key constants and a placeholder
// marker (`CHECKOUT_DELIVERY_DETAILS_CONTENT_ASSET_IDS`,
// `ELIGIBLE_ITEMS_TOTAL_PLACEHOLDER`) with the modal that consumes them.
// They are semantically tied to this modal and used nowhere else, so
// keeping them here documents intent better than a sibling file. Fast
// Refresh will fall back to a full page reload on edits — acceptable
// for a modal that opens on user action.
/* eslint-disable react-refresh/only-export-components */
'use client';

import { Suspense, use } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import type { ContentAssetBody } from '@/lib/api/content-asset';

export type ShippingMethodDetailsVariant = 'delivery' | 'free';

/**
 * Per-delivery-method keys used to discriminate which Business Manager content asset to render
 * inside `<ShippingMethodDetailsModal/>`. Each key corresponds to a distinct Figma variant.
 *
 * "Delivery Details" CTAs (variant=delivery) — Figma 37819:29646
 *   • `whiteGlove`     — White Glove HD (Figma 37293:61437)
 *   • `doorstep`       — Doorstep HD (Figma 37293:61465)
 *   • `thirdParty`     — Premium / Third-Party HD (Figma 37293:61439)
 *   • `groundShipping` — Free Ground Shipping (Figma 37303:75946)
 *
 * "Fee Details" CTAs (variant=free for HD methods) — fee table per value range
 *   • `whiteGloveFeeDetails` — White Glove fee table (highlights row matching cart eligibility)
 *   • `doorstepFeeDetails`   — Doorstep fee table (2 rows)
 *
 * NOTE: third-party "Fee details" reuses `thirdParty` (no separate fee-table asset — third-party
 * is a flat per-item fee, not a value-range table). Ground-shipping "Free Ground Shipping" CTA
 * reuses `groundShipping`.
 */
export type ShippingMethodDetailsAssetKey =
    | 'whiteGlove'
    | 'doorstep'
    | 'thirdParty'
    | 'groundShipping'
    | 'whiteGloveFeeDetails'
    | 'doorstepFeeDetails';

/**
 * Business Manager content asset IDs consumed by `<ShippingMethodDetailsModal/>`, keyed by
 * delivery-method. The checkout loader fetches each asset in parallel and threads the resulting
 * Promises through `contentAssets`. The modal picks the asset whose key matches the active
 * `(variant, methodName)` pair and renders its raw HTML body via `dangerouslySetInnerHTML` —
 * BM authored content is the single source of truth. No i18n / Page Designer fallback exists.
 *
 * If a required asset is missing or unpublished the modal body is empty (Suspense-gated, no
 * flash). The loader already logs per-asset 404/403; surfacing a visible empty state on a
 * missing-asset is intentional — it's the correct failure mode for a CMS-driven modal.
 *
 * Naming convention `checkout-<delivery|fee>-details-<slug>` keeps the assets discoverable in BM
 * (alphabetically grouped) and mirrors the Figma variant names.
 */
export const CHECKOUT_DELIVERY_DETAILS_CONTENT_ASSET_IDS: Record<ShippingMethodDetailsAssetKey, string> = {
    whiteGlove: 'checkout-delivery-details-white-glove',
    doorstep: 'checkout-delivery-details-doorstep',
    thirdParty: 'checkout-delivery-details-third-party',
    groundShipping: 'checkout-delivery-details-ground-shipping',
    whiteGloveFeeDetails: 'checkout-fee-details-white-glove',
    doorstepFeeDetails: 'checkout-fee-details-doorstep',
};

/**
 * Token consumed by `ContentAssetBodyView` to inject the runtime "value of eligible items in
 * your cart" amount into a fee-details content asset. Authors place this literal string inside
 * their HTML body and the modal substitutes it with the formatted currency string before
 * dangerouslySetInnerHTML. JS-template-literal style is chosen for legibility — `${name}` is
 * universally recognized by both engineers and merchandisers as "a variable goes here".
 *
 * Example BM markup:
 *   <p>Value of Eligible Items in Your Cart: <strong>${'$'}{eligibleItemsTotal}</strong></p>
 *
 * Currently consumed only by the `whiteGloveFeeDetails` and `doorstepFeeDetails` assets, but
 * the substitution runs on every asset body so a merchandiser can add the token to any future
 * asset (e.g. a Black-Friday promo content slot) without code changes.
 */
export const ELIGIBLE_ITEMS_TOTAL_PLACEHOLDER = '${eligibleItemsTotal}';
const ELIGIBLE_ITEMS_TOTAL_TOKEN_REGEX = /\$\{eligibleItemsTotal\}/g;

/**
 * Inline style fragment injected into the fee-table row whose value range matches the shopper's
 * runtime eligible-items amount. The fragment is appended to the matching row's existing
 * `style="..."` attribute so authors keep full layout control over the row markup and we only
 * "paint" the row with the active state. Figma marks the active row with bold + the success
 * green token on text only (no background wash) — see the value/fee labels in 37293:61437.
 */
const FEE_ROW_ACTIVE_STYLE = 'font-weight:600;color:var(--success);';

/**
 * Opening-tag regex matching any element that carries both `data-fee-min` and `data-fee-max`
 * attributes (in either order). Captures used by `highlightMatchingFeeRow` to determine the row
 * range and to splice the highlight into the matched row's style attribute.
 *
 *   Group 1 — `data-fee-min` value
 *   Group 2 — `data-fee-max` value (empty string or "UP" treated as +Infinity)
 *
 * The pattern intentionally allows any tag name and any attribute ordering so merchandisers
 * can use whichever element they prefer (div / tr / li) without changing the matching code.
 */
const FEE_ROW_OPEN_REGEX =
    /<[a-zA-Z][^>]*?\bdata-fee-min="([^"]*)"[^>]*?\bdata-fee-max="([^"]*)"[^>]*?>|<[a-zA-Z][^>]*?\bdata-fee-max="([^"]*)"[^>]*?\bdata-fee-min="([^"]*)"[^>]*?>/g;

/**
 * Minimal HTML-attribute / text-node escape. The `eligibleItemsTotal` value comes from
 * `formatCurrency` (locale-aware currency string — digits, separators, currency symbol) so
 * realistic input is HTML-safe, but we escape defensively to keep the substitution from ever
 * turning into a unsafe injection vector for callers that pass arbitrary strings.
 */
function escapeHtmlForSubstitution(value: string): string {
    return value.replace(/[&<>"']/g, (char) => {
        switch (char) {
            case '&':
                return '&amp;';
            case '<':
                return '&lt;';
            case '>':
                return '&gt;';
            case '"':
                return '&quot;';
            case "'":
                return '&#39;';
            default:
                return char;
        }
    });
}

/**
 * Scans the BM-authored body markup for fee-table rows (any element carrying both
 * `data-fee-min` and `data-fee-max` attributes), finds the single row whose value range covers
 * the shopper's `eligibleItemsTotalAmount`, and injects the active-state style fragment into
 * that row's `style="..."` attribute. Returns the body unchanged when:
 *
 *   • `amount` is missing or not a finite number;
 *   • the body contains no `data-fee-min` rows (asset isn't a fee table);
 *   • no row's range covers `amount` (defensive — e.g. negative cart total).
 *
 * Range semantics: min-inclusive, max-inclusive. `data-fee-max=""` or `data-fee-max="UP"` (any
 * casing) → +Infinity (the open-ended top tier in legacy fee tables). Boundary values
 * (e.g. amount exactly equals one row's min) are placed in the lower-min row of the pair —
 * legacy tables author ranges so adjacent tiers don't share boundaries (`499.99` → `500.00`),
 * so this only matters for inputs at the exact threshold.
 *
 * Author contract: each fee row must carry **both** `data-fee-min` and `data-fee-max`. Missing
 * one → silently skipped (so authors can mix decorative rows / sub-headers into the table
 * without them participating in the match).
 */
function highlightMatchingFeeRow(body: string, amount: number | undefined): string {
    if (typeof amount !== 'number' || !Number.isFinite(amount)) {
        return body;
    }
    type RowMatch = { full: string; index: number; min: number; max: number };
    const rows: RowMatch[] = [];
    FEE_ROW_OPEN_REGEX.lastIndex = 0;
    let m: RegExpExecArray | null;
    while ((m = FEE_ROW_OPEN_REGEX.exec(body)) !== null) {
        // Either branch of the alternation matched. min/max are in groups 1+2 OR 4+3.
        const minRaw = m[1] ?? m[4];
        const maxRaw = m[2] ?? m[3];
        const min = Number(minRaw);
        const maxNormalized = maxRaw.trim().toLowerCase();
        const max =
            maxNormalized === '' || maxNormalized === 'up' || maxNormalized === 'infinity'
                ? Number.POSITIVE_INFINITY
                : Number(maxRaw);
        if (!Number.isFinite(min)) continue;
        if (max !== Number.POSITIVE_INFINITY && !Number.isFinite(max)) continue;
        rows.push({ full: m[0], index: m.index, min, max });
    }
    if (rows.length === 0) return body;

    const matching = rows.find(({ min, max }) => amount >= min && amount <= max);
    if (!matching) return body;

    // Append the active-state fragment to the matched row's existing style attribute. If the
    // row has no `style="..."` we add one before the closing `>`. Both branches keep all of
    // the row's existing attributes intact so authors don't lose any layout / event hooks.
    let highlightedRow: string;
    if (/style="[^"]*"/.test(matching.full)) {
        highlightedRow = matching.full.replace(/style="([^"]*)"/, (_, existing: string) => {
            const sep = existing === '' || existing.endsWith(';') ? '' : ';';
            return `style="${existing}${sep}${FEE_ROW_ACTIVE_STYLE}"`;
        });
    } else {
        highlightedRow = matching.full.replace(/>$/, ` style="${FEE_ROW_ACTIVE_STYLE}">`);
    }
    return body.slice(0, matching.index) + highlightedRow + body.slice(matching.index + matching.full.length);
}

/** Map of resolved (or in-flight) content asset Promises keyed by `ShippingMethodDetailsAssetKey`. */
export type ShippingMethodDetailsContentAssets = Partial<
    Record<ShippingMethodDetailsAssetKey, Promise<ContentAssetBody | null>>
>;

export interface ShippingMethodDetailsModalProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    variant?: ShippingMethodDetailsVariant;
    methodName?: string;
    /**
     * Delivery method the modal was opened for (`'HD' | 'DS' | 'DS3PL'`).
     * When provided this discriminates asset selection independently of the
     * translated method name — needed because the HD "White Glove" and DS3PL
     * "White Glove" rows share the same display string but must resolve to
     * different BM assets (HD → `whiteGlove`, DS3PL → `thirdParty`). Falls
     * back to methodName matching when omitted so callers that don't know
     * the group (e.g. OOTB shipping-options picker) still work.
     */
    deliveryMethod?: 'HD' | 'DS' | 'DS3PL';
    /**
     * BM-authored content assets keyed by delivery method (see `ShippingMethodDetailsAssetKey`).
     * The modal selects the matching asset based on its `(variant, methodName)` pair and renders
     * its HTML body. Resolved inside a Suspense boundary so a slow SCAPI call does not block
     * the modal from opening — and so the body fades in alongside the dialog rather than
     * thrashing structured-fallback markup during the close animation.
     */
    contentAssets?: ShippingMethodDetailsContentAssets;
    /**
     * Value of cart items eligible for the active method's fee table. Two forms:
     *
     *   • `amount`    — Raw numeric value used to find the matching fee-table row at runtime.
     *                   `<ContentAssetBodyView/>` walks the body markup for elements carrying
     *                   `data-fee-min` / `data-fee-max` attributes and injects the active-state
     *                   green highlight into the row whose range covers `amount`.
     *   • `formatted` — Pre-formatted currency string (e.g. "$4,387.99") substituted into the
     *                   `${eligibleItemsTotal}` placeholder. Caller owns currency + locale
     *                   formatting so the modal stays stateless and currency-context-free.
     *
     * Both are optional: when undefined the placeholder is stripped (no raw token leaks) and
     * no row gets highlighted. Both are provided together by entry points that mount the modal
     * for a fee-details flow (`<DeliveryGroups/>`); the OOTB ShippingOptions picker omits the
     * prop entirely.
     */
    eligibleItemsTotal?: { amount: number; formatted: string };
}

/**
 * Resolves the content asset promise and renders its body markup. Returns `null` when the asset
 * is missing/unpublished/empty so the modal renders an empty content area instead of structured
 * fallback markup (which would flicker on the close animation when `(variant, methodName)` reset
 * to `undefined` and a different code branch began rendering).
 *
 * Performs TWO transformations on the BM-authored body before injecting:
 *
 *   1. **Placeholder substitution** — replaces every occurrence of `${eligibleItemsTotal}` with
 *      the runtime-provided `formatted` currency string (HTML-escaped). That's what makes the
 *      fee-table assets show the shopper's actual eligible-items total ("Value of Eligible
 *      Items in Your Cart: $4,387.99") instead of the static example baked into BM.
 *   2. **Active-row highlight** — finds the single fee-table row whose `data-fee-min` /
 *      `data-fee-max` range covers the runtime `amount` and appends the green-on-light-green
 *      active-state style to that row's inline `style="..."` attribute. See
 *      `highlightMatchingFeeRow` for the row-matching contract.
 *
 * Both transformations are no-ops when the caller omits `eligibleItemsTotal` (e.g. the OOTB
 * ShippingOptions picker) or when the asset doesn't contain the relevant tokens / attributes
 * (e.g. the rich Delivery Details assets). Substitution is HTML-escaped to keep callers from
 * accidentally creating an injection vector if they pass an unsanitised value.
 *
 * SFCC content assets are authored in BM by trusted merchandisers; the markup is rendered via
 * `dangerouslySetInnerHTML` (same trust model as `<NewsletterSignup/>`). Authors style with
 * inline CSS variables (`var(--foreground)`, `var(--success)`, `var(--destructive)`,
 * `var(--font-serif)`, etc.) which are always resolvable in `:root` — no Tailwind JIT
 * dependency. A baseline `text-foreground` + link styling is kept here as a safety net.
 */
function ContentAssetBodyView({
    promise,
    eligibleItemsTotal,
}: {
    promise: Promise<ContentAssetBody | null>;
    eligibleItemsTotal?: { amount: number; formatted: string };
}) {
    const asset = use(promise);
    const rawBody = asset?.body?.trim();
    if (!rawBody) {
        return null;
    }
    // Strip the token when no runtime value is provided (rather than leaving `${eligibleItemsTotal}`
    // visible in the rendered body) — keeps merchandiser-authored fallback copy clean for variants
    // that don't supply the amount.
    const safeValue = eligibleItemsTotal ? escapeHtmlForSubstitution(eligibleItemsTotal.formatted) : '';
    const bodyAfterPlaceholder = rawBody.replace(ELIGIBLE_ITEMS_TOTAL_TOKEN_REGEX, safeValue);
    const body = highlightMatchingFeeRow(bodyAfterPlaceholder, eligibleItemsTotal?.amount);
    return (
        <div
            className="text-foreground [&_a]:underline [&_a]:underline-offset-2 [&_a]:text-primary [&_a:hover]:text-primary/80"
            data-testid="shipping-method-details-modal-content-asset"
            // SFCC content assets are authored by trusted merchandisers in Business Manager.
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: body }}
        />
    );
}

export function ShippingMethodDetailsModal({
    open,
    onOpenChange,
    variant,
    methodName,
    deliveryMethod,
    contentAssets,
    eligibleItemsTotal,
}: ShippingMethodDetailsModalProps) {
    const { t } = useTranslation('checkout');
    const currentVariant: ShippingMethodDetailsVariant = variant === 'free' ? 'free' : 'delivery';
    const whiteGloveMethodName = t('shipping.method.whiteGlove');
    const doorstepMethodName = t('shipping.method.doorstep');

    /*
     * Resolve which BM content asset corresponds to the active modal state. Two CTA flavors
     * fan into this modal:
     *
     *   "Delivery details" (variant=delivery)
     *     • HD + whiteGlove        → 'whiteGlove'        (rich Figma 37293:61437)
     *     • HD + doorstep          → 'doorstep'          (rich Figma 37293:61465)
     *     • DS3PL (any name)       → 'thirdParty'        (rich Figma 37293:61439)
     *
     *   "Fee details" (variant=free)
     *     • HD + whiteGlove        → 'whiteGloveFeeDetails' (fee table, value-range)
     *     • HD + doorstep          → 'doorstepFeeDetails'   (fee table, 2 rows)
     *     • DS3PL (any name)       → 'thirdParty'           (no fee table — flat fee;
     *                                                        reuse the third-party
     *                                                        delivery-details asset)
     *     • no methodName          → 'groundShipping'       (DS / Free Ground)
     *
     * `deliveryMethod` disambiguates HD White Glove from DS3PL White Glove — they share the
     * same display string but must resolve to different assets. When omitted the resolver
     * falls back to methodName matching for HD/Doorstep + the ground-shipping fee flow.
     *
     * Any other combination resolves to `undefined`, which renders an empty body (the trigger
     * call-site is the right place to guard against an unrecognized state — not a fallback here).
     */
    const assetKey: ShippingMethodDetailsAssetKey | undefined =
        deliveryMethod === 'DS3PL'
            ? 'thirdParty'
            : currentVariant === 'delivery' && methodName === whiteGloveMethodName
              ? 'whiteGlove'
              : currentVariant === 'delivery' && methodName === doorstepMethodName
                ? 'doorstep'
                : currentVariant === 'free' && methodName === whiteGloveMethodName
                  ? 'whiteGloveFeeDetails'
                  : currentVariant === 'free' && methodName === doorstepMethodName
                    ? 'doorstepFeeDetails'
                    : currentVariant === 'free' && !methodName
                      ? 'groundShipping'
                      : undefined;
    const contentAsset = assetKey ? contentAssets?.[assetKey] : undefined;

    /*
     * Screen-reader-only accessible name for the dialog. Radix Dialog requires a non-empty
     * `DialogTitle` / `DialogDescription` for a11y. The visible heading lives inside the BM
     * content asset itself; this sr-only block backstops it. Prefer the active methodName when
     * known so AT users hear something specific (e.g. "White Glove Delivery"); fall back to a
     * generic localized label when the dialog is opened from a non-method-specific CTA.
     */
    const accessibleTitle =
        methodName ||
        (t('shippingOptions.detailsAccessibleTitle', { defaultValue: 'Delivery method details' }) as string);

    const loadingFallback = (
        <div className="space-y-3" aria-hidden="true">
            <Skeleton className="h-6 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-5/6" />
            <Skeleton className="h-4 w-2/3" />
        </div>
    );

    /*
     * Frame width varies by modal flavor:
     *   • Delivery Details   — `sm:max-w-[25rem]` (400px) — locked to Figma 37819:29646
     *                          ("Delivery Details" outer card). The rich Includes / Excludes
     *                          layout was designed at this width; widening it breaks the
     *                          eyebrow + serif heading vertical rhythm and lets the body wrap
     *                          differently than QA approved.
     *   • Fee Details (HD)   — `sm:max-w-[34rem]` (544px) — chosen to fit the descriptive
     *                          prose ("Delivery fees are calculated based on the value of all
     *                          eligible items that are able to be delivered by White Glove
     *                          Delivery") on at most two lines while keeping the dialog from
     *                          feeling oversized in the checkout viewport. With `p-8`
     *                          horizontal padding (64px total) the inner text column is
     *                          ~480px — wraps the prose to two balanced lines and still
     *                          gives the two-column fee table comfortable breathing room.
     *
     * Detected via `assetKey` rather than a separate prop so the call-site stays uniform
     * (one `<ShippingMethodDetailsModal/>` for both flavors). Mobile keeps the
     * `max-w-[calc(100%-2rem)]` page-margin guard regardless of flavor.
     */
    const isFeeDetailsModal = assetKey === 'whiteGloveFeeDetails' || assetKey === 'doorstepFeeDetails';
    const widthClass = isFeeDetailsModal ? 'sm:max-w-[34rem]' : 'sm:max-w-[25rem]';

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className={`w-full max-w-[calc(100%-2rem)] ${widthClass} border border-input rounded-md bg-card shadow-md gap-0 p-0 overflow-hidden`}>
                <DialogHeader className="sr-only">
                    <DialogTitle>{accessibleTitle}</DialogTitle>
                    <DialogDescription>{accessibleTitle}</DialogDescription>
                </DialogHeader>
                <div className="overflow-y-auto max-h-[calc(90vh-120px)] p-8">
                    {contentAsset ? (
                        <Suspense fallback={loadingFallback}>
                            <ContentAssetBodyView promise={contentAsset} eligibleItemsTotal={eligibleItemsTotal} />
                        </Suspense>
                    ) : null}
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default ShippingMethodDetailsModal;
